import { parse } from '@stratumn/canonicaljson';
import { ExternalLink } from 'lucide-react';

import fossilizingImg from '@/assets/fossilizing.svg';
import { EVIDENCE_BATCH_FOSSILIZER } from '@/constant/chainscript';
import type {
  BitcoinAnchorProof,
  Evidence,
  LinkQueryResult
} from '../traceInspector.types';

type Props = {
  link: LinkQueryResult;
};

const explorerNetwork = (provider: string): string => {
  if (provider === 'bcy-test') {
    return 'bcy';
  }
  if (provider.includes('test')) {
    return 'btc-testnet';
  }
  return 'btc';
};

export const SegmentEvidence = ({ link }: Props) => {
  const evidences = link.evidences;

  if (!evidences.totalCount) {
    return (
      <div className="flex flex-col items-center">
        <img alt="" className="my-5 w-7/10" src={fossilizingImg} />
        <div className="font-semibold text-xs">
          The blockchain info for this action is not yet ready. Actions are
          normally recorded every day.
        </div>
      </div>
    );
  }

  /** Rows from the retired fossilizer-api stored the proof as an object. */
  const readAnchorProof = (
    proof: Evidence['proof']['proof']
  ): BitcoinAnchorProof | null => {
    if (typeof proof !== 'string') {
      return proof?.txid ? proof : null;
    }
    try {
      const parsed = parse(Buffer.from(proof, 'base64').toString());
      return parsed?.txid ? parsed : null;
    } catch {
      return null;
    }
  };

  const renderBatchEvidence = (evidence: Evidence, index: number) => {
    const proof = readAnchorProof(evidence.proof.proof);
    if (!proof) {
      return (
        <div
          className="break-words border-border border-b text-muted-foreground text-xs"
          key={`unreadable-${index}`}
        >
          <dl className="last-of-type:mb-4">
            <dt className="inline font-bold">Blockchain:</dt>
            <dd className="inline pl-1">
              This event was recorded, but its blockchain proof cannot be read.
              Contact support with the event hash {link.linkHash}.
            </dd>
          </dl>
        </div>
      );
    }
    return renderProof(proof, evidence.provider);
  };

  const renderProof = (proof: BitcoinAnchorProof, provider: string) => {
    const linkHash = link.linkHash;

    const explorerURL = `https://live.blockcypher.com/${explorerNetwork(provider)}/tx/${proof.txid}`;

    return (
      <div
        className="break-words border-border border-b text-muted-foreground text-xs"
        key={proof.txid}
      >
        <dl className="last-of-type:mb-4">
          <dt className="inline font-bold">Event hash:</dt>
          <dd className="inline pl-1">{linkHash}</dd>
        </dl>
        <dl className="last-of-type:mb-4">
          <dt className="inline font-bold">Blockchain:</dt>
          <dd className="inline pl-1">
            This event hash is independently verifiable on the Bitcoin public
            blockchain. Check on{' '}
            <a
              className="inline-flex items-center gap-1 text-primary-deep no-underline hover:text-primary-deep/80 hover:underline"
              href={explorerURL}
            >
              blockcypher.com
              <ExternalLink size={14} />
            </a>
          </dd>
        </dl>
        <dl className="last-of-type:mb-4">
          <dt className="inline font-bold">Transfer ID:</dt>
          <dd className="inline pl-1">{proof.txid}</dd>
        </dl>
      </div>
    );
  };

  return (
    <>
      {evidences.nodes.map((evidence, index) => {
        switch (evidence.backend) {
          case EVIDENCE_BATCH_FOSSILIZER:
            return renderBatchEvidence(evidence, index);
          default:
            return null;
        }
      })}
    </>
  );
};
